import type { FormInputFormFields } from '@msc-b2c/interfaces/types';
import { ChildInput } from '@msc-b2c/interfaces/types';
import { FormTypeEnum, validateFormField } from '@msc-b2c/shared-utils';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InputText } from '../../../formsComponents/input/InputText';
import { BaseSelect } from '../../../formsComponents/select/BaseSelect';
import { Text } from '../../../text/Text';
import styles from './AvsBillingForm.module.scss';

type AvsBillingFormValues = Record<string, string>;

type AvsField = ChildInput | FormInputFormFields;

type AvsBillingFormProps = {
  childInputs?: AvsField[];
  onValidityChange?: (isValid: boolean) => void;
  onValuesChange?: (values: AvsBillingFormValues) => void;
};

export const AvsBillingForm = ({
  childInputs = [],
  onValidityChange,
  onValuesChange,
}: Readonly<AvsBillingFormProps>) => {
  const textComponents = useMemo(
    () => childInputs.filter((field) => field?.type === 'text-component' && Boolean(field?.text)),
    [childInputs]
  );

  const sectionTitle = textComponents[0]?.text;
  const sectionTitleClassName = textComponents[0]?.appliedCssClassNames ?? '';
  const mandatoryLabel = textComponents[1]?.text;
  const mandatoryLabelClassName = textComponents[1]?.appliedCssClassNames ?? '';

  const fields = useMemo(
    () =>
      childInputs.filter(
        (field) =>
          Boolean(field?.name) &&
          (field.type === FormTypeEnum.TEXT ||
            field.type === FormTypeEnum.DROPDOWN ||
            field.type === FormTypeEnum.ZIPCODE)
      ),
    [childInputs]
  );

  const defaultValues = useMemo(
    () =>
      fields.reduce<AvsBillingFormValues>((acc, field) => {
        if (field.type === FormTypeEnum.DROPDOWN) {
          const selectedOption = field.dropdownItems?.find((item) => item.selected);
          acc[field.name] = selectedOption?.value ?? '';
          return acc;
        }

        acc[field.name] = '';
        return acc;
      }, {}),
    [fields]
  );

  const {
    register,
    control,
    formState: { errors, isValid },
    trigger,
    clearErrors,
    watch,
    reset,
  } = useForm<AvsBillingFormValues>({
    mode: 'onChange',
    defaultValues,
  });

  const values = watch();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (fields.length > 0) {
      trigger();
    }
  }, [fields.length, trigger]);

  useEffect(() => {
    onValuesChange?.(values);
    onValidityChange?.(fields.length === 0 || isValid);
  }, [values, isValid, fields.length, onValuesChange, onValidityChange]);

  if (fields.length === 0) {
    return null;
  }

  return (
    <div className={styles['avsForm']}>
      {sectionTitle && (
        <Text
          tagHtml={sectionTitle.includes('<') ? 'div' : 'p'}
          text={sectionTitle}
          styleOption={`cmp-paymentCard__formSection-title ${sectionTitleClassName}`}
        />
      )}

      <div className={styles['avsForm__fields']}>
        {fields.map((field) => {
          if (field.type === FormTypeEnum.DROPDOWN) {
            return (
              <div
                key={field.name}
                className={styles['avsForm__field']}
              >
                <Controller
                  name={field.name}
                  control={control}
                  rules={{
                    required: field.isMandatory ? field.isMandatoryErrorMessage : false,
                    validate: (value) =>
                      validateFormField(
                        String(value ?? ''),
                        field.validationRulesContainer ?? [],
                        {}
                      ),
                  }}
                  render={({ field: controllerField }) => (
                    <BaseSelect
                      label={''}
                      placeholder={field.placeHolderSelect ?? ''}
                      ariaLabel={field.label}
                      items={field.dropdownItems ?? []}
                      errors={errors}
                      value={String((controllerField.value as string) ?? '')}
                      fieldName={controllerField.name}
                      onChange={controllerField.onChange}
                      onBlur={() => {
                        controllerField.onBlur();
                        trigger(field.name);
                      }}
                      disabled={field.readOnly}
                      clearErrors={clearErrors}
                      useStandardUI={field.useNativeSelect ?? false}
                      styleOption={field.appliedCssClassNames ?? ''}
                    />
                  )}
                />
              </div>
            );
          }

          return (
            <div
              key={field.name}
              className={styles['avsForm__field']}
            >
              <InputText
                register={register}
                errors={errors}
                index={0}
                label={field.label}
                placeholder={field.helpMessage ?? ''}
                name={field.name}
                validation={{
                  validate: (value) =>
                    validateFormField(
                      String(value ?? ''),
                      field.validationRulesContainer ?? [],
                      {}
                    ),
                  onBlur: () => {
                    trigger(field.name);
                  },
                }}
                disabled={field.readOnly}
                clearErrors={clearErrors}
                required={field.isMandatory}
                styleOptions={field.appliedCssClassNames ?? ''}
                isLowerLabel={true}
              />
            </div>
          );
        })}
      </div>

      {mandatoryLabel && (
        <Text
          tagHtml={mandatoryLabel.includes('<') ? 'div' : 'span'}
          text={mandatoryLabel}
          styleOption={`${styles['avsForm__mandatory']} ${mandatoryLabelClassName}`}
        />
      )}
    </div>
  );
};
